/**
 * GatewayCompression — kaynak-referans transport yığınından 1-1 portlandı (o
 * uyuyan yığın 2026-08-11'de söküldü; canlı yol budur).
 * Erlang gateway `compress=zstd-stream&stream=1` negotiate edilince
 * bağlantı ömrü boyunca TEK süregelen zstd akışını (ZSTD_e_flush, ASLA
 * ZSTD_e_end) hem gönderirken hem alırken sürdürür — bkz. tebhos_gateway
 * `_build/.../ezstd/src/ezstd.erl` compress_streaming/2. Bunun için
 * libtebhoscore WASM'ının streaming decoder/encoder (DCtx/CCtx) handle'ları
 * bağlantı boyunca TEK örnek olarak tutulur.
 *
 * ÖNCEKİ SÜRÜM (SÖKÜLDÜ): native `DecompressionStream("deflate")` (zlib)
 * tabanlıydı — Erlang'ın anlamadığı bir format, hiçbir yere bağlı değildi
 * (yalnız kendi testinde kullanılıyordu, gerçek gateway trafiğine hiç
 * girmedi). Bu dosya artık kaynak-referans paritesiyle GERÇEK zstd-stream kullanıyor.
 */

import {
  compressZstdStreamChunk,
  createZstdStreamDecoder,
  createZstdStreamEncoderSync,
  decompressZstdFrame,
  decompressZstdStreamChunk,
  ensureLibTebhosCoreReady,
  freeZstdStreamDecoder,
  freeZstdStreamEncoder,
  isLibTebhosCoreReady,
} from "./LibTebhosCoreWasm";

export type CompressionType = "none" | "zstd-stream";

const ZSTD_STREAM_LEVEL = 3;

export class GatewayCompressionError extends Error {
  readonly compression: CompressionType;
  readonly originalError: unknown;

  constructor(compression: CompressionType, originalError: unknown) {
    super(`Gateway ${compression} decompression failed`);
    this.name = "GatewayCompressionError";
    this.compression = compression;
    this.originalError = originalError;
  }
}

export function isGatewayCompressionError(error: unknown): error is GatewayCompressionError {
  return error instanceof GatewayCompressionError;
}

/** Tercih edilen compression — kaynak-referans 1-1: her zaman zstd-stream (WASM her yerde mevcut). */
export function getPreferredCompression(): CompressionType {
  return "zstd-stream";
}

export function isCompressionSupported(type: CompressionType): boolean {
  switch (type) {
    case "none":
    case "zstd-stream":
      return true;
    default:
      return false;
  }
}

export class GatewayCompression {
  private readonly type: CompressionType;
  private readonly stream: boolean;
  private zstdStreamDecoder: number | null = null;
  private zstdStreamEncoder: number | null = null;
  private pendingStreamOperations = 0;
  private destroyed = false;
  private streamQueue: Promise<void> = Promise.resolve();

  constructor(type: CompressionType, stream: boolean = type === "zstd-stream") {
    this.type = type;
    this.stream = stream;
  }

  canCompress(): boolean {
    return this.type === "zstd-stream" && this.stream;
  }

  async warmup(): Promise<void> {
    if (!this.canCompress()) return;
    await ensureLibTebhosCoreReady();
    if (this.destroyed) return;
    if (this.zstdStreamEncoder == null) {
      this.zstdStreamEncoder = createZstdStreamEncoderSync(ZSTD_STREAM_LEVEL);
    }
  }

  /** Giden mesajı sıkıştırır (bağlantı boyunca sürdürülen CCtx ile flush edilir). */
  compress(text: string): Uint8Array {
    if (!this.canCompress()) {
      throw new GatewayCompressionError(this.type, new Error("Gateway compression is not enabled"));
    }
    if (this.destroyed) {
      throw new GatewayCompressionError(this.type, new Error("Gateway compression codec is destroyed"));
    }
    if (!isLibTebhosCoreReady()) {
      throw new GatewayCompressionError(this.type, new Error("Gateway compression encoder is not ready"));
    }
    try {
      if (this.zstdStreamEncoder == null) {
        this.zstdStreamEncoder = createZstdStreamEncoderSync(ZSTD_STREAM_LEVEL);
      }
      return compressZstdStreamChunk(this.zstdStreamEncoder, new TextEncoder().encode(text));
    } catch (error) {
      throw new GatewayCompressionError(this.type, error);
    }
  }

  async decompress(data: ArrayBuffer): Promise<string> {
    const input = new Uint8Array(data);
    switch (this.type) {
      case "none":
        return new TextDecoder().decode(input);
      case "zstd-stream":
        return this.stream ? this.decompressZstdStream(input) : this.decompressZstdFrameOnce(input);
      default:
        throw new Error(`Unsupported compression type: ${this.type}`);
    }
  }

  private async decompressZstdFrameOnce(data: Uint8Array): Promise<string> {
    let wasmDecoded: Uint8Array;
    try {
      wasmDecoded = await decompressZstdFrame(data);
    } catch (error) {
      throw new GatewayCompressionError(this.type, error);
    }
    return new TextDecoder().decode(wasmDecoded);
  }

  /**
   * kaynak-referans 1-1: WS `onmessage` çağrıları senkron sırayla gelmeyebilir
   * (browser event loop) — tek mutable DCtx'e erişimi bir promise-kuyruğu ile
   * SERİLEŞTİRİYORUZ ki flush sınırları asla yarışmasın.
   */
  private async decompressZstdStream(data: Uint8Array): Promise<string> {
    const operation = this.streamQueue.then(() => this.decompressZstdStreamLocked(data));
    this.streamQueue = operation.then(
      () => undefined,
      () => undefined
    );
    return operation;
  }

  private async decompressZstdStreamLocked(data: Uint8Array): Promise<string> {
    this.pendingStreamOperations++;
    try {
      if (this.destroyed) {
        throw new Error("Gateway compression decoder is destroyed");
      }
      if (this.zstdStreamDecoder == null) {
        const decoder = await createZstdStreamDecoder();
        if (this.destroyed) {
          freeZstdStreamDecoder(decoder);
          throw new Error("Gateway compression decoder is destroyed");
        }
        this.zstdStreamDecoder = decoder;
      }
      const decompressed = await decompressZstdStreamChunk(this.zstdStreamDecoder, data);
      return new TextDecoder().decode(decompressed);
    } catch (error) {
      throw new GatewayCompressionError(this.type, error);
    } finally {
      this.pendingStreamOperations--;
      this.freeDestroyedStreamDecoder();
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.freeDestroyedStreamDecoder();
    if (this.zstdStreamEncoder != null) {
      freeZstdStreamEncoder(this.zstdStreamEncoder);
      this.zstdStreamEncoder = null;
    }
  }

  private freeDestroyedStreamDecoder(): void {
    if (!this.destroyed || this.pendingStreamOperations > 0 || this.zstdStreamDecoder == null) return;
    freeZstdStreamDecoder(this.zstdStreamDecoder);
    this.zstdStreamDecoder = null;
  }
}
